import React, { useMemo, useRef, useState } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import clsx from 'clsx';

import Icon from '../Icon';
import Button from '../Button';
import Input from '../Input';
import { usePopupPosition } from '@hooks';

type ArrowProps = {
  className?: string;
  direction?: 'up' | 'down';
};

const Arrow: React.FC<ArrowProps> = ({ className, direction = 'down' }) => {
  return (
    <Icon
      width
      icon="angledown"
      className={clsx(className, 'text-current')}
      iconClassName={clsx(
        'transition-all',
        direction === 'down' ? '' : 'rotate-180'
      )}
    />
  );
};

export type DropdownProps<T = string> = {
  value?: T;
  className?: string;
  placeholder?: string;
  withArrow?: boolean;
  arrowInside?: boolean;
  withCustomValue?: boolean;
  nullable?: boolean;
  emptyMessage?: string;
  closeOnSelect?: boolean;
  dock?: 'left' | 'right';

  open?: boolean;
  onToggle?: (open: boolean) => void;
  disabled?: boolean;

  grow?: boolean;
  popupClassName?: string;
  optionClassName?: string;
  wrapperClassName?: string;
  inputRoundedClassName?: string;

  renderValue?: (params: {
    value?: T;
    placeholder?: string;
    label?: React.ReactNode;
    inputRoundedClassName?: string;
  }) => React.ReactNode;
  renderOption?: (params: {
    index: number;
    disabled: boolean;
    optionClassName?: string;
    option: OptionType<T>;
    options: OptionType<T>[];
    value: OptionType<T>['value'] | undefined;
    handleSelect: (value: OptionType<T>['value']) => void;
  }) => React.ReactNode;

  options: OptionType<T>[];
  onChange?: (newValue: T, option?: OptionType<T>) => void;
};

const defaultRenderValue = <T,>({
  label,
  value,
  placeholder,
  inputRoundedClassName,
}: {
  value?: T;
  placeholder?: string;
  label?: React.ReactNode;
  inputRoundedClassName?: string;
}) => (
  <Input
    isChild
    readonly
    withBorder
    className="w-full"
    placeholder={placeholder}
    defaultSizeClass=""
    inputClassName="pr-2xl cursor-pointer"
    inputRoundedClassName={inputRoundedClassName}
    value={
      typeof label === 'string' ? label : typeof value === 'string' ? value : ''
    }
  />
);

const defaultRenderOption = <T,>({
  value,
  disabled,
  option: o,
  optionClassName,
  handleSelect,
}: {
  index: number;
  disabled: boolean;
  optionClassName?: string;
  option: OptionType<T>;
  options: OptionType<T>[];
  value: OptionType<T>['value'] | undefined;
  handleSelect: (value: OptionType<T>['value']) => void;
}) => {
  return (
    <div
      key={String(o.value)}
      onClick={e => {
        e.stopPropagation();
        handleSelect(o.value);
      }}
      className={clsx(
        'rounded-sm hover:bg-active px-sm py-xs transition-all duration-150',
        optionClassName,
        'truncate shrink-0',
        o.value === value ? 'text-text bg-active' : 'text-text',
        disabled ? 'pointer-events-none' : 'cursor-pointer'
      )}
    >
      {o.label}
    </div>
  );
};

export const Dropdown = <T,>({
  value,
  options: initialOptions,
  className,
  placeholder,
  withCustomValue = false,
  nullable = false,
  withArrow = true,
  arrowInside = true,
  closeOnSelect = true,
  emptyMessage = 'No options',
  open: controlledOpen,

  grow = true,
  optionClassName,
  wrapperClassName,
  inputRoundedClassName = 'rounded-sm',
  popupClassName = 'max-h-[22.5rem] overflow-y-auto rounded-sm',
  onToggle,
  onChange,
  renderValue = defaultRenderValue,
  renderOption = defaultRenderOption,
  disabled = false,
}: DropdownProps<T>): React.JSX.Element => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const refAnchorRef = useRef<any>(null);
  const {
    popupRef,
    anchorRef,

    popupPosition,
    anchorPosition,
  } = usePopupPosition({ enable: open });

  const options = useMemo<OptionType<T>[]>(() => {
    const enrichedOptions = (() => {
      if (!value) return initialOptions;
      if (initialOptions.some(o => o.value === value)) {
        return initialOptions;
      }

      return [...initialOptions, { value, label: String(value) }];
    })();

    return nullable
      ? ([
          { value: '', label: '\u00A0' },
          ...enrichedOptions,
        ] as unknown as OptionType<T>[])
      : enrichedOptions;
  }, [value, initialOptions, nullable]);

  const handleClose = () => {
    if (controlledOpen === undefined) {
      setInternalOpen(false);
    }
    onToggle?.(false);
  };
  const handleToggle = () => {
    if (!disabled) {
      if (controlledOpen === undefined) {
        setInternalOpen(o => !o);
      }
      onToggle?.(!open);
    }
  };

  const handleSelect = (newValue: T) => {
    if (!disabled) {
      onChange?.(
        newValue,
        options?.find(o => o.value === newValue)
      );
      !!closeOnSelect && handleClose();
    }
  };

  const [customInputValue, setCustomInputValue] = useState('');

  const handleAddCustomValue = () => {
    const newValue = customInputValue.trim();
    if (!newValue) return;

    handleSelect(newValue as unknown as T);
    setCustomInputValue('');
  };

  useOnClickOutside(refAnchorRef, handleClose);

  const option = options?.find(o => o.value === value);
  const label = option?.valueLabel || option?.label || '';

  return (
    <div
      ref={node => {
        node && anchorRef(node);
        refAnchorRef.current = node;
      }}
      className={clsx(
        'relative text-small',
        className,
        inputRoundedClassName,
        grow ? 'grow' : 'sm:flex-1',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-between gap-x-3 w-full',
          wrapperClassName
        )}
        onClick={handleToggle}
      >
        {renderValue({
          value,
          label,
          placeholder,
          inputRoundedClassName,
        })}
        {!arrowInside && !!withArrow && (
          <Arrow direction={open ? 'up' : 'down'} />
        )}
      </div>
      {!!arrowInside && !!withArrow && (
        <div className="absolute right-[0.5rem] top-0 h-full inline-flex items-center">
          <Arrow direction={open ? 'up' : 'down'} />
        </div>
      )}
      {!disabled && (
        <div
          ref={popupRef}
          style={{
            left: popupPosition?.style?.left ?? 0,
            top: popupPosition?.style?.top ?? 0,
            minWidth: anchorPosition.width ?? 0,
            maxWidth: 300,
          }}
          className={clsx(
            'fixed z-popper py-sm',
            open ? 'visible' : 'invisible'
          )}
        >
          <div
            className={clsx(
              popupClassName,
              'shadow-sm border bg-content border-active p-xs',
              'transition-non-position',
              'flex flex-col gap-y-sm',
              open ? 'opacity-100' : 'opacity-0'
            )}
          >
            {options.map((option, index) =>
              renderOption({
                index,
                value,
                option,
                options,
                disabled,
                optionClassName,
                handleSelect,
              })
            )}
            {withCustomValue && (
              <div onClick={e => e.stopPropagation()}>
                <Input
                  isChild
                  withBorder
                  className="w-full"
                  value={customInputValue}
                  defaultSizeClass=""
                  inputClassName="pr-[7rem]"
                  inputRoundedClassName={inputRoundedClassName}
                  placeholder={placeholder ?? 'Enter custom value'}
                  onChange={setCustomInputValue}
                  onSubmit={handleAddCustomValue}
                  disabled={disabled}
                  suffix={
                    <Button
                      round
                      icon="plus"
                      type="light"
                      width="small"
                      className="shrink-0"
                      onClick={e => {
                        e.stopPropagation();
                        handleAddCustomValue();
                      }}
                    />
                  }
                />
              </div>
            )}
            {!options.length && !withCustomValue && !!emptyMessage && (
              <div className="px-sm py-xs pointer-events-none cursor-default">
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
